import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { api, releaseId } from "../../lib/api";

/**
 * VoteButtons — thumbs-up (green) + thumbs-down (red) with average score.
 *
 * `initialStats` and `initialMine` may be pre-fetched in bulk by the parent
 * (recommended when rendering >20 cards) to avoid an N+1 request storm.
 */
export const VoteButtons = ({
  release,
  compact = false,
  initialStats = null,
  initialMine = null,
}) => {
  const rid = releaseId(release);
  const [stats, setStats] = useState(
    initialStats || { up: 0, down: 0, total: 0, score: 0 }
  );
  const [mine, setMine] = useState(initialMine);
  const [pending, setPending] = useState(false);
  const [loaded, setLoaded] = useState(initialStats !== null);

  // Only released items are votable (release_date <= today).
  const isReleased = (() => {
    const rd = release?.release_date;
    if (!rd) return false;
    return rd <= new Date().toISOString().slice(0, 10);
  })();

  // Fetch individually only if parent did not preload
  useEffect(() => {
    if (loaded) return;
    let alive = true;
    Promise.all([
      api.get(`/votes/${encodeURIComponent(rid)}`),
      api.get(`/votes/${encodeURIComponent(rid)}/mine`),
    ])
      .then(([s, m]) => {
        if (!alive) return;
        setStats(s.data);
        setMine(m.data.value);
        setLoaded(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [rid, loaded]);

  // If parent updates preloaded data (e.g. filter changes), keep in sync
  useEffect(() => {
    if (initialStats) setStats(initialStats);
  }, [initialStats]);
  useEffect(() => {
    if (initialMine !== undefined) setMine(initialMine);
  }, [initialMine]);

  useEffect(() => {
    const sync = (e) => {
      if (e.detail.rid !== rid) return;
      setStats(e.detail.stats);
      setMine(e.detail.mine);
      setLoaded(true);
    };
    window.addEventListener("rp:vote", sync);
    return () => window.removeEventListener("rp:vote", sync);
  }, [rid]);

  const vote = async (value) => {
    if (pending) return;
    setPending(true);
    try {
      const { data } = await api.post(`/votes/${encodeURIComponent(rid)}`, {
        release_id: rid,
        value,
      });
      const nextMine = mine === value ? null : value;
      setStats(data);
      setMine(nextMine);
      window.dispatchEvent(new CustomEvent("rp:vote", { detail: { rid, stats: data, mine: nextMine } }));
    } finally {
      setPending(false);
    }
  };

  const pct = stats.total > 0 ? Math.round(stats.score * 100) : null;

  if (!isReleased) {
    return (
      <div
        data-testid={`vote-locked-${rid}`}
        className={`inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-neutral-500 ${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-[0.15em]`}
      >
        À venir · Vote à l'ouverture
      </div>
    );
  }

  return (
    <div
      data-testid={`vote-${rid}`}
      className={`flex items-center gap-2 ${compact ? "text-xs" : "text-sm"}`}
    >
      <button
        data-testid={`vote-up-${rid}`}
        onClick={() => vote("up")}
        disabled={pending}
        aria-label="J'ai aimé"
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 transition-colors ${
          mine === "up"
            ? "border-emerald-600 bg-emerald-50 text-emerald-700"
            : "border-neutral-200 text-neutral-700 hover:border-emerald-500 hover:text-emerald-700"
        }`}
      >
        <ThumbsUp className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"}`} />
        <span className="tabular-nums">{stats.up}</span>
      </button>
      <button
        data-testid={`vote-down-${rid}`}
        onClick={() => vote("down")}
        disabled={pending}
        aria-label="Je n'ai pas aimé"
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 transition-colors ${
          mine === "down"
            ? "border-[var(--red)] bg-[var(--red-soft)] text-[var(--red)]"
            : "border-neutral-200 text-neutral-700 hover:border-[var(--red)] hover:text-[var(--red)]"
        }`}
      >
        <ThumbsDown className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"}`} />
        <span className="tabular-nums">{stats.down}</span>
      </button>
      {pct !== null && (
        <span
          data-testid={`vote-score-${rid}`}
          className={`inline-flex items-center gap-1 ${compact ? "text-[10px]" : "text-xs"} text-neutral-500`}
        >
          <span className="font-medium text-neutral-900 tabular-nums">{pct}%</span>
          <span className="uppercase tracking-widest">aimé</span>
        </span>
      )}
    </div>
  );
};
